import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { toast } from "react-toastify";
import type { RootState } from "@/store";
import { clearChatSession } from "@/services/chatSession";
import {
  applicationServerKey,
  chatNotificationRequest,
  claimChatToast,
  PUSH_OWNER_KEY,
  pushSupported,
  readPushOwner,
  revokeChatPush,
  withPushLock,
  writePushOwner,
} from "@/helpers/chatPush";

type AlertItem = {
  id: string;
  messageId: string;
  conversationId: string;
  authorName: string;
  conversationName: string;
};
export type ChatNotificationState = {
  status: "unsupported" | "off" | "denied" | "on" | "busy";
  error: string | null;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
};
const foreground = () =>
  !document.hidden && document.hasFocus() && navigator.onLine !== false;

export function useChatNotifications(
  activeConversationId: string | null,
): ChatNotificationState {
  const store = useStore<RootState>();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const { generation, blocked, conversationVersions } = useSelector(
    (state: RootState) => state.chatSession,
  );
  const actorId = user?.id;
  const active = useRef(activeConversationId);
  active.current = activeConversationId;
  const [status, setStatus] = useState<ChatNotificationState["status"]>("off");
  const [error, setError] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const busy = useRef(false);
  const toastIds = useRef(new Map<string, string>());
  const current = useCallback(() => {
    const state = store.getState();
    return (
      state.auth.user?.id === actorId &&
      state.auth.token === token &&
      state.chatSession.generation === generation &&
      !state.chatSession.blocked
    );
  }, [store, actorId, token, generation]);

  useEffect(() => {
    toastIds.current.forEach((conversationId, id) => {
      if (blocked || conversationVersions[conversationId]) {
        toast.dismiss(id);
        toastIds.current.delete(id);
      }
    });
  }, [conversationVersions, blocked]);

  useEffect(() => {
    if (!actorId || !token) return;
    const update = () => {
      if (!current() || busy.current) return;
      if (!pushSupported()) {
        setStatus("unsupported");
        return;
      }
      const owner = readPushOwner();
      setSubscriptionId(
        owner?.actorId === actorId ? owner.subscriptionId : null,
      );
      setStatus(
        Notification.permission === "denied"
          ? "denied"
          : owner?.actorId === actorId && Notification.permission === "granted"
            ? "on"
            : "off",
      );
    };
    update();
    if (pushSupported())
      void withPushLock(async () => {
        if (!current()) return;
        const owner = readPushOwner();
        const registration =
          owner?.actorId === actorId
            ? await navigator.serviceWorker.register(
                "/chat-notifications-sw.js",
                { scope: "/" },
              )
            : await navigator.serviceWorker.getRegistration("/");
        const subscription = await registration?.pushManager.getSubscription();
        if (!current()) return;
        if (
          owner &&
          (owner.actorId !== actorId ||
            !subscription ||
            Notification.permission !== "granted")
        ) {
          await subscription?.unsubscribe();
          writePushOwner(null);
          setSubscriptionId(null);
          setStatus(Notification.permission === "denied" ? "denied" : "off");
        }
      }).catch(() => {});
    // Observe identity synchronously, before the keyed provider unmounts on logout.
    let revoked = false;
    const unsubscribe = store.subscribe(() => {
      if (!current() && !revoked) {
        revoked = true;
        toastIds.current.forEach((_conversation, id) => toast.dismiss(id));
        toastIds.current.clear();
        if (pushSupported())
          void revokeChatPush(actorId, token).catch(() => {});
      }
    });
    const onStorage = (event: StorageEvent) => {
      if (event.key === PUSH_OWNER_KEY) update();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", update);
    return () => {
      unsubscribe();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", update);
      toastIds.current.forEach((_conversation, id) => toast.dismiss(id));
      toastIds.current.clear();
    };
  }, [actorId, token, current, store]);

  const enable = useCallback(async () => {
    if (!actorId || !token || !current() || !pushSupported() || busy.current)
      return;
    busy.current = true;
    // The permission prompt must start directly in the user's click handler (Safari).
    setStatus("busy");
    setError(null);
    try {
      const permission = Notification.requestPermission();
      if ((await permission) !== "granted") {
        if (current())
          setStatus(Notification.permission === "denied" ? "denied" : "off");
        return;
      }
      await withPushLock(async () => {
        if (!current()) return;
        const { publicKey } = await chatNotificationRequest(
          token,
          "/chat/push-settings",
        );
        if (!current()) return;
        const registration = await navigator.serviceWorker.register(
          "/chat-notifications-sw.js",
          { scope: "/" },
        );
        await navigator.serviceWorker.ready;
        if (!current()) return;
        const previous = await registration.pushManager.getSubscription();
        if (previous) await previous.unsubscribe();
        writePushOwner(null);
        setSubscriptionId(null);
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(publicKey),
        });
        if (!current()) {
          await subscription.unsubscribe();
          return;
        }
        try {
          const result = await chatNotificationRequest(
            token,
            "/chat/push-subscriptions",
            {
              method: "POST",
              body: JSON.stringify({ subscription: subscription.toJSON() }),
            },
          );
          if (!current()) {
            await subscription.unsubscribe();
            return;
          }
          writePushOwner({ actorId, subscriptionId: result.id });
          setSubscriptionId(result.id);
          setStatus("on");
        } catch (reason) {
          await subscription.unsubscribe();
          throw reason;
        }
      });
    } catch {
      if (current()) {
        setStatus("off");
        setError(
          "Activation impossible. Vérifiez votre connexion et réessayez.",
        );
      }
    } finally {
      busy.current = false;
    }
  }, [actorId, token, current]);
  const disable = useCallback(async () => {
    if (!actorId || !token || busy.current) return;
    busy.current = true;
    setStatus("busy");
    setError(null);
    try {
      await revokeChatPush(actorId, token);
      if (current()) {
        setSubscriptionId(null);
        setStatus("off");
      }
    } catch {
      if (current()) {
        setStatus("on");
        setError("Désactivation impossible. Réessayez.");
      }
    } finally {
      busy.current = false;
    }
  }, [actorId, token, current]);

  useEffect(() => {
    if (!actorId || !token || blocked) return;
    let stopped = false,
      running = false,
      cursor: string | undefined,
      failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const clientId = subscriptionId ? window.crypto.randomUUID() : "";
    let presenceQueue = Promise.resolve();
    const presence = (visible: boolean) => {
      presenceQueue = presenceQueue.then(() =>
        subscriptionId
          ? chatNotificationRequest(token, "/chat/push-presence", {
              method: "POST",
              keepalive: !visible,
              body: JSON.stringify({
                subscriptionId,
                clientId,
                foreground: visible,
              }),
            }).catch((reason) => {
              if (
                reason.status === 404 &&
                current() &&
                readPushOwner()?.subscriptionId === subscriptionId
              ) {
                writePushOwner(null);
                setSubscriptionId(null);
                setStatus("off");
                setError(
                  "Cet abonnement a expiré. Activez à nouveau les notifications.",
                );
              }
            })
          : Promise.resolve(),
      );
      return presenceQueue;
    };
    const tick = async () => {
      clearTimeout(timer);
      if (stopped || running || !foreground() || !current()) return;
      running = true;
      const versions = store.getState().chatSession.conversationVersions;
      controller = new AbortController();
      try {
        const result = await chatNotificationRequest(
          token,
          `/chat/notifications${cursor === undefined ? "" : `?after=${encodeURIComponent(cursor)}`}`,
          { signal: controller.signal },
        );
        if (
          stopped ||
          !foreground() ||
          !current() ||
          versions !== store.getState().chatSession.conversationVersions
        )
          return;
        if (typeof result.cursor !== "string" || !Array.isArray(result.data))
          throw new Error("Invalid feed");
        cursor = result.cursor;
        failures = 0;
        await presence(true);
        if (stopped || !current()) return;
        for (const item of result.data as AlertItem[]) {
          if (!current() || stopped || !foreground()) break;
          if (item.conversationId === active.current) continue;
          if (!(await claimChatToast(actorId, item.id))) continue;
          if (
            !current() ||
            stopped ||
            !foreground() ||
            versions !== store.getState().chatSession.conversationVersions
          )
            break;
          const id = `chat:${actorId}:${item.id}`;
          toastIds.current.set(id, item.conversationId);
          toast.info(
            <a
              className="chat-notification-toast"
              href={`/chat/${encodeURIComponent(item.conversationId)}?message=${encodeURIComponent(item.messageId)}`}
              onClick={() => toast.dismiss(id)}
            >
              <strong>{item.conversationName}</strong>
              <span>{item.authorName} a envoyé un message.</span>
              <span className="chat-notification-open">
                Ouvrir la conversation
              </span>
            </a>,
            {
              toastId: id,
              autoClose: 7000,
              onClose: () => {
                toastIds.current.delete(id);
              },
            },
          );
        }
      } catch (reason) {
        failures++;
        const status = (reason as { status?: number }).status;
        if ((status === 401 || status === 403) && current())
          store.dispatch(clearChatSession({ actorId, blocked: true }));
      } finally {
        running = false;
        if (!stopped && foreground() && current())
          timer = setTimeout(
            tick,
            Math.min(3000 * Math.pow(2, failures), 30000),
          );
      }
    };
    const visibility = () => {
      if (!foreground()) {
        clearTimeout(timer);
        controller?.abort();
        cursor = undefined;
        void presence(false);
      } else {
        void tick();
      }
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("focus", visibility);
    window.addEventListener("blur", visibility);
    window.addEventListener("online", visibility);
    window.addEventListener("offline", visibility);
    const pagehide = () => {
      clearTimeout(timer);
      controller?.abort();
      void presence(false);
    };
    window.addEventListener("pagehide", pagehide);
    void tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller?.abort();
      void presence(false);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("focus", visibility);
      window.removeEventListener("blur", visibility);
      window.removeEventListener("online", visibility);
      window.removeEventListener("offline", visibility);
      window.removeEventListener("pagehide", pagehide);
    };
  }, [actorId, token, generation, blocked, current, store, subscriptionId]);
  return { status, error, enable, disable };
}
